import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { getDestinationLogoPath } from '../../../utils/destination-logos';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, forkJoin } from 'rxjs';
import { DestinationsApiService, DestinationTypeDto, DestinationDto, canExtendToFamily } from '../../../services/destinations-api.service';
import { AudienceApiService } from '../../../services/audience-api.service';
import { NotificationService } from '../../../services/notification.service';
import { AuthService } from '../../../services/auth.service';
import { CustomSelectComponent, CustomSelectOption } from '../../shared/custom-select/custom-select.component';
import { buildTtdParentOptions } from '../../../utils/ttd-taxonomy';
import { environment } from '../../../../environments/environment';

interface Country {
  code: string;
  name: string;
}

const DESTINATION_ALERTS: Record<string, string> = {
  GAM: 'destinations.activation.alerts.GAM',
};

@Component({
  selector: 'app-destination-activation-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, CustomSelectComponent],
  templateUrl: './destination-activation-modal.component.html',
  styleUrl: './destination-activation-modal.component.scss'
})
export class DestinationActivationModalComponent implements OnInit, OnDestroy {
  @Input() audienceId: string | null = null;
  @Input() audienceName: string | null = null;
  @Input() entityName: string | null = null;
  @Input() entityId: string | null = null;
@Output() close = new EventEmitter<void>();
  @Output() activated = new EventEmitter<void>();

  destinationTypes: DestinationTypeDto[] = [];
  allDestinations: DestinationDto[] = [];
  typesLoading = false;
  destinationsLoading = false;
  isSubmitting = false;
  isRequestingActivation = false;
  expandedTypeId: number | null = null;
  selectedDestinationIds = new Set<number>();
  countries: Country[] = [];
  ttdParentNodeOptions: CustomSelectOption[] = [];
  ttdTaxonomyLoading = false;
  private ttdTaxonomyDestinationId: number | null = null;
  activationForm!: FormGroup;
  requestActivationForm!: FormGroup;
  brokenLogoIds = new Set<number>();
  /**
   * Entity chosen inside the modal when the destination needs one but the sidebar is on
   * Global Data Scope (no entity selected). Feeds the activation's EntityID.
   */
  selectedEntityId: string | null = null;

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private destinationsApi: DestinationsApiService,
    private audienceApi: AudienceApiService,
    private notificationService: NotificationService,
    private translate: TranslateService,
    private authService: AuthService
  ) {
    this.activationForm = this.fb.group({});
    this.requestActivationForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.loadData();
    this.loadCountries();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get isLoading(): boolean {
    return this.typesLoading || this.destinationsLoading;
  }

  get expandedType(): DestinationTypeDto | null {
    if (this.expandedTypeId === null) return null;
    return this.destinationTypes.find(t => t.ID === this.expandedTypeId) ?? null;
  }

  get activationParamsType(): string | null {
    return this.expandedType?.ActivationParamsType ?? null;
  }

  get showActivationForm(): boolean {
    return this.selectedDestinationIds.size > 0;
  }

  get hasGlobalDataScope(): boolean {
    return this.authService.hasGlobalDataScope();
  }

  get hasGlobalDataAccess(): boolean {
    return this.authService.hasGlobalDataAccess();
  }

  /**
   * Destinations that cannot be activated against the global scope: they need a real entity,
   * so under Global Data Scope we ask for one inside the modal instead of blocking the user.
   */
  get requiresEntitySelection(): boolean {
    return this.activationParamsType === 'AudienceRealtime' && this.hasGlobalDataScope;
  }

  /** True while the entity is still missing: everything else in the panel stays hidden. */
  get isEntitySelectionPending(): boolean {
    return this.requiresEntitySelection && !this.selectedEntityId;
  }

  get entityOptions(): CustomSelectOption[] {
    const dcrs = this.authService.currentUserValue?.availableDCRs ?? [];
    return [...dcrs]
      .map(dcr => ({ value: dcr.id, label: dcr.Name || dcr.name || dcr.id }))
      .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
  }

  onEntitySelected(entityId: string | null): void {
    this.selectedEntityId = entityId || null;
    if (!this.selectedEntityId) return;
    // The form defaults (e.g. AudienceRealtime's OwnerID) depend on the entity, so rebuild it.
    // ExcludeControlGroup/ExtendToFamily live outside those per-entity defaults, so carry over whatever the
    // admin already picked instead of letting the rebuild silently reset them to unchecked.
    if (this.selectedDestinationIds.size > 0) {
      const excludeControlGroup = this.activationForm.get('ExcludeControlGroup')?.value;
      const extendToFamily = this.activationForm.get('ExtendToFamily')?.value;
      const activationNote = this.activationForm.get('ActivationNote')?.value;
      this.buildActivationForm();
      this.activationForm.get('ExcludeControlGroup')?.setValue(!!excludeControlGroup);
      this.activationForm.get('ExtendToFamily')?.setValue(!!extendToFamily);
      this.activationForm.get('ActivationNote')?.setValue(activationNote ?? '');
    }
  }

  get requestActivationParamsType(): string {
    return this.expandedType?.ActivationParamsType || 'Generic';
  }

  get showRequestActivationForm(): boolean {
    if (this.expandedTypeId === null) return false;
    const type = this.destinationTypes.find(t => t.ID === this.expandedTypeId);
    if (!type) return false;
    return this.getDestinationsForType(type).length === 0;
  }

  getAlertForType(type: DestinationTypeDto): string | null {
    return DESTINATION_ALERTS[type.ActivationType] ?? null;
  }

  toggleType(id: number): void {
    if (this.expandedTypeId === id) {
      this.expandedTypeId = null;
      this.selectedDestinationIds.clear();
      this.selectedEntityId = null;
      this.activationForm = this.fb.group({});
      this.requestActivationForm = this.fb.group({});
      return;
    }
    this.expandedTypeId = id;
    this.selectedDestinationIds.clear();
    this.selectedEntityId = null;
    this.activationForm = this.fb.group({});
    this.requestActivationForm = this.fb.group({});
    const type = this.destinationTypes.find(t => t.ID === id);
    if (type) {
      const destinations = this.getDestinationsForType(type);
      if (destinations.length === 1) {
        this.selectedDestinationIds.add(destinations[0].ID);
        this.buildActivationForm();
      } else if (destinations.length === 0) {
        this.buildRequestActivationForm();
      }
    }
  }

  isExpanded(id: number): boolean {
    return this.expandedTypeId === id;
  }

  getTypeIconPath(type: DestinationTypeDto): string | null {
    if (this.brokenLogoIds.has(type.ID)) return null;
    return getDestinationLogoPath(type.ID);
  }

  onLogoError(typeId: number): void {
    this.brokenLogoIds.add(typeId);
  }

  private typeLabel(type: DestinationTypeDto): string {
    return (type.Name || type.Description || type.ActivationType).toLowerCase();
  }

  getDestinationsForType(type: DestinationTypeDto): DestinationDto[] {
    return this.allDestinations
      .filter(d => d.DestinationTypeID === type.ID)
      .sort((a, b) => (a.Name ?? '').toLowerCase().localeCompare((b.Name ?? '').toLowerCase()));
  }

  get typesWithDestinations(): DestinationTypeDto[] {
    return this.destinationTypes
      .filter(t => this.getDestinationsForType(t).length > 0 || t.PermissionType === 'assisted-service')
      .sort((a, b) => this.typeLabel(a).localeCompare(this.typeLabel(b)));
  }

  get typesWithoutDestinations(): DestinationTypeDto[] {
    return this.destinationTypes
      .filter(t => this.getDestinationsForType(t).length === 0 && t.PermissionType !== 'assisted-service')
      .sort((a, b) => this.typeLabel(a).localeCompare(this.typeLabel(b)));
  }

  isSoleDestination(destinationId: number): boolean {
    if (this.expandedTypeId === null) return false;
    const type = this.destinationTypes.find(t => t.ID === this.expandedTypeId);
    if (!type) return false;
    return this.getDestinationsForType(type).length === 1;
  }

  toggleDestination(destinationId: number, checked: boolean): void {
    if (!checked && this.isSoleDestination(destinationId)) return;
    if (checked) {
      this.selectedDestinationIds.add(destinationId);
    } else {
      this.selectedDestinationIds.delete(destinationId);
    }
    if (this.selectedDestinationIds.size === 1 && checked) {
      this.buildActivationForm();
    } else if (this.selectedDestinationIds.size === 0) {
      this.activationForm = this.fb.group({});
    }
    this.syncExtendToFamilyControl();
  }

  isDestinationSelected(destinationId: number): boolean {
    return this.selectedDestinationIds.has(destinationId);
  }

  /** Selected destinations ExtendToFamily won't be applied to (they export ip_landline). One submit creates
   *  one activation per selected destination, and they may export different ID types. */
  get extendToFamilyExcludedDestinations(): DestinationDto[] {
    return this.allDestinations.filter(d => this.selectedDestinationIds.has(d.ID) && !canExtendToFamily(d.ExportIDType));
  }

  get extendToFamilyExcludedNames(): string {
    return this.extendToFamilyExcludedDestinations.map(d => d.Name).join(', ');
  }

  /** Disables the checkbox when no selected destination can be extended, re-enables it otherwise. */
  private syncExtendToFamilyControl(): void {
    const control = this.activationForm?.get('ExtendToFamily');
    if (!control) return;
    const excluded = this.extendToFamilyExcludedDestinations.length;
    if (this.selectedDestinationIds.size > 0 && excluded === this.selectedDestinationIds.size) {
      control.setValue(false);
      control.disable({ emitEvent: false });
    } else {
      control.enable({ emitEvent: false });
    }
  }

  get rateLevelValue(): string {
    return this.activationForm.get('RateLevel')?.value ?? '';
  }

  get countryOptions(): CustomSelectOption[] {
    return this.countries.map(c => ({ value: c.code, label: c.name }));
  }

  get rateLevelOptions(): CustomSelectOption[] {
    return [
      { value: 'System', label: 'System' },
      { value: 'Partner', label: 'Partner' },
      { value: 'Advertiser', label: 'Advertiser' },
    ];
  }

  get zeotapAccountTypeValue(): string {
    return this.activationForm.get('AccountType')?.value ?? '';
  }

  get zeotapAccountTypeOptions(): CustomSelectOption[] {
    return [
      { value: 'Marketplace', label: 'Marketplace' },
      { value: 'Advertiser', label: 'Advertiser' },
    ];
  }

  get ttdCurrencyOptions(): CustomSelectOption[] {
    return [{ value: 'USD', label: 'USD' }];
  }

  /** Parent-node picker for ExternalSegmentParentID: cached TTD taxonomy nodes, plus an explicit "use the destination's default root" option (empty value — the BE already falls back to it when this is blank). */
  get ttdParentOptions(): CustomSelectOption[] {
    return [
      { value: '', label: this.translate.instant('destinations.fields.externalSegmentParentIdDefault') },
      ...this.ttdParentNodeOptions
    ];
  }

  refreshTtdParentOptions(): void {
    if (this.ttdTaxonomyDestinationId) {
      this.loadTtdParentOptions(this.ttdTaxonomyDestinationId, true);
    }
  }

  get ttd1pdRegionOptions(): CustomSelectOption[] {
    return [
      { value: 'US', label: 'US' },
      { value: 'EU', label: 'EU' },
    ];
  }

  get ttd1pdSegmentTypeOptions(): CustomSelectOption[] {
    return [
      { value: 'TARGETING', label: 'Targeting' },
      { value: 'CONVERSION', label: 'Conversion' },
      { value: 'TARGETING_DIRECT_IP', label: 'Targeting Direct IP' },
    ];
  }

  get dv360AccountTypeOptions(): CustomSelectOption[] {
    return [
      { value: 'advertiser', label: 'Advertiser' },
      { value: 'partner', label: 'Partner' },
    ];
  }

  get pubmaticRequestAccountTypeOptions(): CustomSelectOption[] {
    return [
      { value: 'advertiser', label: 'Advertiser' },
      { value: 'publisher', label: 'Publisher' },
    ];
  }

  get ttdRequestAccountTypeOptions(): CustomSelectOption[] {
    return [
      { value: 'advertiser', label: 'Advertiser' },
      { value: 'partner', label: 'Partner' },
    ];
  }

  get ttd1pdMergeModeOptions(): CustomSelectOption[] {
    return [
      { value: '', label: 'None' },
      { value: 'ADD', label: 'Add' },
      { value: 'REPLACE', label: 'Replace' },
    ];
  }

  onSubmit(): void {
    if (this.activationForm.invalid || !this.audienceId || !this.selectedDestinationIds.size) return;
    if (this.isEntitySelectionPending) return;
    this.isSubmitting = true;

    const { ExcludeControlGroup, ExtendToFamily, ActivationNote, ...channelData } = this.activationForm.getRawValue();
    const description = (ActivationNote ?? '').toString().trim();

    // When an admin activates without a specific entity selected in the main
    // selector ("Admin" global scope), the backend still requires an EntityID.
    // An entity picked inside the modal wins; otherwise default it to the
    // "_TNC Data" entity so the activation can be attributed.
    const entityId = this.authService.hasGlobalDataScope()
      ? (this.selectedEntityId ?? this.entityId ?? environment.TNCDataEntityId ?? null)
      : this.entityId;

    const calls = Array.from(this.selectedDestinationIds).map(destinationId =>
      this.audienceApi.addActivationItem({
        AudienceID: this.audienceId!,
        DestinationID: destinationId,
        ChannelData: channelData,
        ...(entityId ? { EntityID: entityId } : {}),
        ...(description ? { Description: description } : {}),
        ...(this.hasGlobalDataAccess ? {
          ExcludeControlGroup: !!ExcludeControlGroup,
          ExtendToFamily: !!ExtendToFamily && canExtendToFamily(this.allDestinations.find(d => d.ID === destinationId)?.ExportIDType)
        } : {}),
      })
    );

    const sub = forkJoin(calls).subscribe({
      next: () => {
        this.notificationService.notify('success',
          this.translate.instant('destinations.messages.activated', { name: this.audienceName ?? '' }));
        this.activated.emit();
        this.closeModal();
        this.isSubmitting = false;
      },
      error: (error) => {
        console.error('[destination-activation] Activation failed', error);
        this.notificationService.notify('error',
          error?.message || this.translate.instant('destinations.activation.error'));
        this.isSubmitting = false;
      }
    });
    this.subscriptions.add(sub);
  }

  onRequestActivation(): void {
    if (!this.audienceId || !this.expandedTypeId || this.isRequestingActivation) return;
    this.isRequestingActivation = true;

    const channelData = this.requestActivationForm.getRawValue();

    const payload: {
      AudienceID: string;
      DestinationTypeID: number;
      EntityID?: string;
      ChannelData?: Record<string, unknown>;
      ExportIDType?: string[];
    } = {
      AudienceID: this.audienceId,
      DestinationTypeID: this.expandedTypeId,
    };

    const entityId = this.entityId ?? this.authService.currentUserValue?.entityID ?? null;
    if (entityId) payload.EntityID = entityId;
    if (Object.keys(channelData).length > 0) payload.ChannelData = channelData;
    const exportIdType = this.expandedType?.ExportIDType;
    if (exportIdType) payload.ExportIDType = [exportIdType];

    const sub = this.audienceApi.requestActivation(payload).subscribe({
      next: () => {
        this.notificationService.notify('success',
          this.translate.instant('destinations.activation.requestSent'));
        this.closeModal();
        this.isRequestingActivation = false;
      },
      error: (error) => {
        console.error('[destination-activation] Request activation failed', error);
        this.notificationService.notify('error',
          error?.message || this.translate.instant('destinations.activation.requestError'));
        this.isRequestingActivation = false;
      }
    });
    this.subscriptions.add(sub);
  }

  closeModal(): void {
    this.close.emit();
  }

  private buildActivationForm(): void {
    switch (this.activationParamsType) {

      case 'ExternalSegment':
      case 'AdformAPI':
      case 'Generic':
      case 'generic':
        this.activationForm = this.fb.group({
          ExternalSegmentID: [this.audienceId ?? ''],
        });
        break;

      case 'Pubmatic':
        this.activationForm = this.fb.group({
          ExternalSegmentID: [this.audienceId ?? ''],
          PubmaticAudienceID: [''],
          Name: [this.audienceName ?? ''],
          Description: [''],
          Country: ['IT'],
          Price: [''],
        });
        break;

      case 'TheTradeDesk': {
        const form = this.fb.group({
          CustomAudienceName: [this.audienceName ?? '', Validators.required],
          CustomAudienceDesc: [''],
          ExternalSegmentID: [this.audienceId ?? ''],
          ExternalSegmentParentID: [null],
          CPMRateAmount: [0.6, Validators.required],
          CPMRateCurrencyCode: ['USD', Validators.required],
          PercentOfMediaCostRate: [0.15, Validators.required],
          RateLevel: ['System', Validators.required],
          AdvertiserId: [{ value: null, disabled: true }],
          PartnerId: [{ value: null, disabled: true }],
          Buyable: [true, Validators.required],
        });
        const rateSub = form.controls['RateLevel'].valueChanges.subscribe(value => {
          switch (value) {
            case 'Advertiser':
              form.controls['AdvertiserId'].setValidators([Validators.required]);
              form.controls['AdvertiserId'].enable();
              form.controls['PartnerId'].clearValidators();
              form.controls['PartnerId'].disable();
              break;
            case 'Partner':
              form.controls['PartnerId'].setValidators([Validators.required]);
              form.controls['PartnerId'].enable();
              form.controls['AdvertiserId'].clearValidators();
              form.controls['AdvertiserId'].disable();
              break;
            default:
              form.controls['AdvertiserId'].clearValidators();
              form.controls['AdvertiserId'].disable();
              form.controls['PartnerId'].clearValidators();
              form.controls['PartnerId'].disable();
          }
          form.controls['AdvertiserId'].updateValueAndValidity();
          form.controls['PartnerId'].updateValueAndValidity();
        });
        this.subscriptions.add(rateSub);
        this.activationForm = form;
        const ttdDestinationId = Array.from(this.selectedDestinationIds)[0];
        if (ttdDestinationId) {
          this.loadTtdParentOptions(ttdDestinationId);
        }
        break;
      }

      case 'TheTradeDesk1pd':
        this.activationForm = this.fb.group({
          CustomAudienceName: [this.audienceName ?? '', Validators.required],
          Region: ['', Validators.required],
          SegmentType: ['', Validators.required],
          MergeMode: [null],
          AdvertiserID: [null],
        });
        break;

      case 'TheTradeDeskOfflineConversion':
        // ExternalSegmentID (the TTD TrackingTagId) is assigned by the backend when the tag is
        // created - it must NOT be sent on creation. addActivation treats a truthy incoming
        // ExternalSegmentID as "tag already exists" and skips the create call, so no placeholder
        // value here (unlike TheTradeDesk/Zeotap, where ExternalSegmentID isn't used as that signal).
        this.activationForm = this.fb.group({
          TrackingTagName: [this.audienceName ?? '', Validators.required],
        });
        break;

      case 'AudienceRealtime':
        this.activationForm = this.fb.group({
          ExposedID: [this.audienceId ?? ''],
          ExternalAudienceName: [this.audienceName ?? ''],
          Description: [this.audienceName ?? ''],
          OwnerID: [
            this.selectedEntityId
              ?? this.authService.getActiveDcrId()
              ?? this.authService.currentUserValue?.entityID
              ?? '',
            Validators.required
          ],
          TTL: [91, Validators.required],
        });
        break;

      case 'Zeotap': {
        const form = this.fb.group({
          AccountType: ['Marketplace', Validators.required],
          ExternalSegmentID: ['', Validators.required],
          SegmentName: [{ value: this.audienceName ?? '', disabled: true }],
          Description: [{ value: '', disabled: true }],
          Price: [{ value: null, disabled: true }],
          Countries: [{ value: ['IT'], disabled: true }],
          ZeotapDestinationID: [{ value: '', disabled: true }],
        });
        const accountTypeSub = form.controls['AccountType'].valueChanges.subscribe(value => {
          switch (value) {
            case 'Advertiser':
              form.controls['ExternalSegmentID'].clearValidators();
              form.controls['ExternalSegmentID'].disable();
              form.controls['SegmentName'].setValidators([Validators.required]);
              form.controls['SegmentName'].enable();
              form.controls['Description'].enable();
              form.controls['Price'].setValidators([Validators.required, Validators.min(0)]);
              form.controls['Price'].enable();
              form.controls['Countries'].setValidators([Validators.required]);
              form.controls['Countries'].enable();
              form.controls['ZeotapDestinationID'].setValidators([Validators.required]);
              form.controls['ZeotapDestinationID'].enable();
              break;
            default: // 'Marketplace'
              form.controls['ExternalSegmentID'].setValidators([Validators.required]);
              form.controls['ExternalSegmentID'].enable();
              form.controls['SegmentName'].clearValidators();
              form.controls['SegmentName'].disable();
              form.controls['Description'].disable();
              form.controls['Price'].clearValidators();
              form.controls['Price'].disable();
              form.controls['Countries'].clearValidators();
              form.controls['Countries'].disable();
              form.controls['ZeotapDestinationID'].clearValidators();
              form.controls['ZeotapDestinationID'].disable();
          }
          (['ExternalSegmentID', 'SegmentName', 'Description', 'Price', 'Countries', 'ZeotapDestinationID'] as const)
            .forEach(key => form.controls[key].updateValueAndValidity());
        });
        this.subscriptions.add(accountTypeSub);
        this.activationForm = form;
        break;
      }

      case 'Onetag':
        this.activationForm = this.fb.group({
          ExternalSegmentID: [this.audienceId ?? ''],
          SegmentName: [this.audienceName ?? '', Validators.required],
          Price: [null],
          Currency: ['EUR'],
        });
        break;

      default:
        this.activationForm = this.fb.group({
          ExternalSegmentID: [this.audienceId ?? ''],
        });
    }

    this.activationForm.addControl('ActivationNote', this.fb.control(''));
    this.activationForm.addControl('ExcludeControlGroup', this.fb.control(false));
    this.activationForm.addControl('ExtendToFamily', this.fb.control(false));
    this.syncExtendToFamilyControl();
  }

  private buildRequestActivationForm(): void {
    switch (this.requestActivationParamsType) {

      case 'Adform':
        this.requestActivationForm = this.fb.group({
          Country: ['', Validators.required],
          AccountName: ['', Validators.required],
        });
        break;

      case 'AudienceRate':
        this.requestActivationForm = this.fb.group({
          AccountType: ['', Validators.required],
          AccountID: ['', Validators.required],
        });
        break;

      case 'Pubmatic':
        this.requestActivationForm = this.fb.group({
          AccountType: ['', Validators.required],
          AccountID: ['', Validators.required],
        });
        break;

      case 'TheTradeDesk':
      case 'TheTradeDesk1pd':
        this.requestActivationForm = this.fb.group({
          AccountType: ['', Validators.required],
          AccountID: ['', Validators.required],
        });
        break;

      case 'Infosum':
        this.requestActivationForm = this.fb.group({
          CompanyID: ['', Validators.required],
        });
        break;

      case 'Zeotap':
        this.requestActivationForm = this.fb.group({
          AdvertiserName: ['', Validators.required],
        });
        break;

      default:
        this.requestActivationForm = this.fb.group({});
    }
  }

  private loadData(): void {
    this.typesLoading = true;
    this.destinationsLoading = true;

    const typesSub = this.destinationsApi.getDestinationTypes().subscribe({
      next: (types) => {
        this.destinationTypes = types ?? [];
        this.typesLoading = false;
      },
      error: (error) => {
        console.error('[destination-activation] Failed to load destination types', error);
        this.notificationService.notify('error', this.translate.instant('destinations.messages.loadError'));
        this.typesLoading = false;
      }
    });

    const destsSub = this.destinationsApi.getDestinations({
      skipDcr: this.authService.hasGlobalDataAccess()
    }).subscribe({
      next: (destinations) => {
        this.allDestinations = destinations ?? [];
        this.destinationsLoading = false;
      },
      error: (error) => {
        console.error('[destination-activation] Failed to load destinations', error);
        this.destinationsLoading = false;
      }
    });

    this.subscriptions.add(typesSub);
    this.subscriptions.add(destsSub);
  }

  private loadCountries(): void {
    const sub = this.audienceApi.getCountries().subscribe({
      next: (countries) => { this.countries = countries; },
      error: (err) => { console.error('[destination-activation] Failed to load countries', err); }
    });
    this.subscriptions.add(sub);
  }

  private loadTtdParentOptions(destinationId: number, refresh = false): void {
    this.ttdTaxonomyDestinationId = destinationId;
    this.ttdTaxonomyLoading = true;
    const sub = this.destinationsApi.getTtdTaxonomyTree({ DestinationID: destinationId, Refresh: refresh }).subscribe({
      next: (res) => {
        this.ttdParentNodeOptions = buildTtdParentOptions(res.Elements ?? []);
        this.ttdTaxonomyLoading = false;
      },
      error: (err) => {
        console.error('[destination-activation] Failed to load TTD taxonomy', err);
        this.ttdParentNodeOptions = [];
        this.ttdTaxonomyLoading = false;
      }
    });
    this.subscriptions.add(sub);
  }

}
